import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { FormFieldModel, FormFieldCrmMap, CrmObjectType, CrmUpdateRule, FieldType } from '../../../models/form-field.model';
import { MarketingFormsService, CrmStandardField, CatalogOption } from '../../../service/marketing-forms.service';
import { FormFieldOption } from '../../../models/form-field.model';
import { FIELD_CAPS } from '../../../utils/field-caps.util';
import { ToastService } from '../../../../../shared/services/toast.service';
import { extractApiErrorMessage } from '../../../utils/api-error.util';

export interface FieldCrmMapPair {
  field: FormFieldModel;
  mapping: FormFieldCrmMap | null;
}

@Component({
  selector: 'app-form-crm-mapping-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './form-crm-mapping-panel.component.html',
})
export class FormCrmMappingPanelComponent implements OnChanges, OnDestroy {
  @Input() rows: FieldCrmMapPair[] = [];
  @Input() loading = false;
  @Input() hasUnsavedFields = false;
  @Input() formId!: number;

  @Output() mappingChanged = new EventEmitter<{ fieldId: number; mapping: FormFieldCrmMap | null }>();
  @Output() fixedValueChanged = new EventEmitter<{ fieldId: number; value: string }>();
  @Output() catalogOptionsSync = new EventEmitter<{ fieldId: number; options: FormFieldOption[] }>();

  // ── Panel state ───────────────────────────────────────────────────────────
  activeRow: FieldCrmMapPair | null = null;
  activeTab: CrmObjectType = 'CONTACT';
  search = '';
  advancedOpen = false;
  pendingStdField: string | undefined;
  pendingUpdateRule: CrmUpdateRule = 'ONLY_IF_EMPTY';
  pendingFixedValue = '';
  saving = false;

  catalogOptions: CatalogOption[] = [];
  catalogLoading = false;
  private catalogCache: Record<string, CatalogOption[]> = {};

  /** Cache de campos estándar por objectType. `undefined` = no cargado aún. */
  standardFieldsMap: Partial<Record<CrmObjectType, CrmStandardField[]>> = {};

  get selectedPropCatalog(): string | null {
    const fields = this.standardFieldsMap[this.activeTab] ?? [];
    return fields.find(f => f.key === this.pendingStdField)?.catalog ?? null;
  }

  get showCatalogPicker(): boolean {
    return this.selectedPropCatalog != null && this.activeRow?.field.fieldType === 'HIDDEN';
  }

  private readonly destroy$ = new Subject<void>();

  // ── Constants ─────────────────────────────────────────────────────────────

  readonly entityTabs: { value: CrmObjectType; label: string }[] = [
    { value: 'CONTACT', label: 'Contactos' },
    { value: 'COMPANY', label: 'Empresas'  },
    { value: 'TICKET',  label: 'Tickets'   },
  ];

  readonly updateRules: { value: CrmUpdateRule; label: string }[] = [
    { value: 'ONLY_IF_EMPTY',    label: 'Solo si está vacío'    },
    { value: 'ALWAYS_OVERWRITE', label: 'Siempre sobrescribir'  },
    { value: 'APPEND',           label: 'Concatenar (append)'   },
  ];

  private readonly FIELD_TYPE_LABELS: Record<FieldType, string> = {
    TEXT:               'Texto corto',
    TEXTAREA:           'Texto largo',
    EMAIL:              'Correo electrónico',
    PHONE:              'Teléfono',
    NUMBER:             'Número',
    DATE:               'Fecha',
    DROPDOWN:           'Lista desplegable',
    CHECKBOXES:         'Casillas',
    RADIO:              'Opción múltiple',
    FILE_UPLOAD:        'Archivo',
    GDPR_CONSENT:       'Consentimiento',
    HIDDEN:             'Campo oculto',
    RATING_NPS:         'Puntuación NPS',
    GEO_COUNTRY_REGION: 'País/Región',
  };

  readonly FIELD_ICONS: Record<FieldType, string> = {
    TEXT:               'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    TEXTAREA:           'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    EMAIL:              'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    PHONE:              'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
    NUMBER:             'M7 20l4-16m2 16l4-16M6 9h14M4 15h14',
    DATE:               'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    DROPDOWN:           'M8 9l4-4 4 4m0 6l-4 4-4-4',
    CHECKBOXES:         'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    RADIO:              'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    FILE_UPLOAD:        'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
    GDPR_CONSENT:       'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    HIDDEN:             'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.477 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21',
    RATING_NPS:         'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    GEO_COUNTRY_REGION: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  };

  readonly CRM_PROP_ICONS: Record<string, string> = {
    TEXT:   'M4 6h16M4 12h16M4 18h7',
    EMAIL:  'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    PHONE:  'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
    NUMBER: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14',
    URL:    'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  };

  private readonly OBJECT_LABELS: Record<CrmObjectType, string> = {
    CONTACT: 'Contacto',
    COMPANY: 'Empresa',
    DEAL:    'Negocio',
    LEAD:    'Lead',
    TICKET:  'Ticket',
  };

  /**
   * Tipos de propiedad CRM compatibles por tipo de campo de formulario.
   * Los tipos de propiedad CRM disponibles en el catálogo son: TEXT, EMAIL, PHONE, NUMBER, URL.
   * Array vacío = el tipo no tiene propiedades compatibles en el catálogo estándar.
   */
  private readonly FORM_TO_CRM_COMPAT: Record<FieldType, readonly string[]> = {
    TEXT:               ['TEXT', 'URL'],
    TEXTAREA:           ['TEXT'],
    EMAIL:              ['EMAIL'],
    PHONE:              ['PHONE', 'TEXT'],
    NUMBER:             ['NUMBER'],
    DATE:               [],             // sin propiedades DATE en el catálogo estándar
    DROPDOWN:           ['TEXT'],
    CHECKBOXES:         ['TEXT'],
    RADIO:              ['TEXT'],
    HIDDEN:             ['TEXT', 'EMAIL', 'PHONE', 'URL', 'NUMBER'],
    RATING_NPS:         ['NUMBER'],
    GEO_COUNTRY_REGION: ['TEXT'],
    FILE_UPLOAD:        [],             // crmMap:false, no llega aquí
    GDPR_CONSENT:       [],             // crmMap:false, no llega aquí
  };

  private readonly objectCopy: Partial<Record<CrmObjectType, { label: string; crear: string }>> = {
    CONTACT: { label: 'Contacto', crear: 'el contacto' },
    COMPANY: { label: 'Empresa',  crear: 'la empresa'  },
    TICKET:  { label: 'Ticket',   crear: 'el ticket'   },
  };

  constructor(
    private formsService: MarketingFormsService,
    private toast: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows']) {
      this.preloadMappedObjectTypes();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Stats ─────────────────────────────────────────────────────────────────

  get mappableRows(): FieldCrmMapPair[] {
    return this.rows.filter(r => FIELD_CAPS[r.field.fieldType]?.crmMap);
  }

  get totalFields(): number   { return this.mappableRows.length; }
  get mappedCount(): number   { return this.mappableRows.filter(r => !!r.mapping?.crmObjectType).length; }
  get unmappedCount(): number { return this.totalFields - this.mappedCount; }
  get hasRequiredFields(): boolean { return this.mappableRows.some(r => r.field.isRequired); }

  // ── Table helpers ─────────────────────────────────────────────────────────

  fieldTypeLabel(type: FieldType): string {
    return this.FIELD_TYPE_LABELS[type] ?? type;
  }

  propertyLabel(row: FieldCrmMapPair): string {
    const m = row.mapping;
    if (!m?.crmObjectType) return '';
    if (m.isCustomAttribute) return 'Atributo personalizado';
    const fields = this.standardFieldsMap[m.crmObjectType] ?? [];
    const found = fields.find(f => f.key === m.crmStandardField);
    return found?.label ?? m.crmStandardField ?? '';
  }

  objectLabel(type: CrmObjectType): string {
    return this.OBJECT_LABELS[type] ?? type;
  }

  /** Tipo de dato de la propiedad CRM, en español. */
  private readonly CRM_PROP_TYPE_LABELS: Record<string, string> = {
    TEXT: 'Texto',
    EMAIL: 'Correo electrónico',
    PHONE: 'Teléfono',
    NUMBER: 'Número',
    URL: 'Enlace',
    DATE: 'Fecha',
    BOOLEAN: 'Sí / No',
    ENUM: 'Lista de opciones',
  };

  crmPropTypeLabel(type: string | null | undefined): string {
    if (!type) return '';
    return this.CRM_PROP_TYPE_LABELS[type.toUpperCase()] ?? type;
  }

  crmPropIcon(type: string): string {
    return this.CRM_PROP_ICONS[type?.toUpperCase()] ?? this.CRM_PROP_ICONS['TEXT'];
  }

  isActiveRow(row: FieldCrmMapPair): boolean {
    return this.activeRow?.field.id === row.field.id;
  }

  // ── Warnings ──────────────────────────────────────────────────────────────

  get missingRequiredWarnings(): string[] {
    const messages: string[] = [];
    const usedTypes = new Set(
      this.mappableRows
        .filter(r => r.mapping?.crmObjectType && !r.mapping.isCustomAttribute)
        .map(r => r.mapping!.crmObjectType),
    );
    for (const objType of usedTypes) {
      const fields = this.standardFieldsMap[objType] ?? [];
      const requiredFields = fields.filter(f => f.required);
      if (!requiredFields.length) continue;
      const mappedKeys = new Set(
        this.mappableRows
          .filter(r => r.mapping?.crmObjectType === objType && !r.mapping?.isCustomAttribute)
          .map(r => r.mapping!.crmStandardField)
          .filter(Boolean),
      );
      if (!mappedKeys.size) continue;
      const copy = this.objectCopy[objType] ?? { label: objType, crear: `el ${objType}` };
      for (const rf of requiredFields) {
        if (!mappedKeys.has(rf.key)) {
          messages.push(
            `Estás mapeando campos a ${copy.label}, pero ningún campo está mapeado a ` +
            `'${rf.label}' (obligatorio para crear ${copy.crear}).`,
          );
        }
      }
    }
    return messages;
  }

  // ── Panel open/close ──────────────────────────────────────────────────────

  openRow(row: FieldCrmMapPair): void {
    this.activeRow        = row;
    this.search           = '';
    this.advancedOpen     = false;
    this.catalogOptions   = [];
    this.catalogLoading   = false;
    this.pendingFixedValue = row.field.defaultFixedValue ?? '';
    const mappedType      = row.mapping?.crmObjectType;
    const validTab        = mappedType && this.entityTabs.some(t => t.value === mappedType);
    if (validTab) {
      this.activeTab         = mappedType!;
      this.pendingStdField   = row.mapping!.crmStandardField ?? undefined;
      this.pendingUpdateRule = row.mapping!.updateRule ?? 'ONLY_IF_EMPTY';
    } else {
      this.activeTab         = 'CONTACT';
      this.pendingStdField   = undefined;
      this.pendingUpdateRule = 'ONLY_IF_EMPTY';
    }
    const fieldToLoad = this.pendingStdField;
    this.ensureStandardFields(this.activeTab, () => {
      if (fieldToLoad) this.loadCatalogForProp(fieldToLoad);
    });
  }

  closePanel(): void {
    this.activeRow = null;
  }

  onTabChange(tab: CrmObjectType): void {
    if (this.activeTab === tab) return;
    this.activeTab        = tab;
    this.pendingStdField  = undefined;
    this.pendingFixedValue = '';
    this.catalogOptions   = [];
    this.ensureStandardFields(tab);
  }

  onPropertySelect(key: string): void {
    this.pendingStdField  = key;
    this.pendingFixedValue = '';
    this.loadCatalogForProp(key);
  }

  isSelected(key: string): boolean {
    return this.pendingStdField === key;
  }

  get isLoadingCurrentTab(): boolean {
    return this.standardFieldsMap[this.activeTab] === undefined;
  }

  /** Propiedades del tab filtradas por compatibilidad de tipo con el campo de formulario activo. */
  get compatibleProperties(): CrmStandardField[] {
    const fields = this.standardFieldsMap[this.activeTab] ?? [];
    if (!this.activeRow) return fields;
    const fieldType = this.activeRow.field.fieldType;
    const allowed = this.FORM_TO_CRM_COMPAT[fieldType];
    if (!allowed || allowed.length === 0) return [];
    const byType = fields.filter(f => allowed.includes((f.type ?? 'TEXT').toUpperCase()));
    if (fieldType === 'HIDDEN') return byType.filter(f => f.catalog != null);
    if (['DROPDOWN', 'CHECKBOXES', 'RADIO'].includes(fieldType) && this.activeTab === 'COMPANY') {
      return byType.filter(f => ['id_company_tax_type', 'id_company_size', 'id_company_sector'].includes(f.key));
    }
    return byType.filter(f => f.catalog == null);
  }

  /** true cuando el tab tiene propiedades pero ninguna es compatible con el tipo del campo activo. */
  get hasIncompatibleType(): boolean {
    const allFields = this.standardFieldsMap[this.activeTab] ?? [];
    return allFields.length > 0 && this.compatibleProperties.length === 0;
  }

  get filteredProperties(): CrmStandardField[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.compatibleProperties;
    return this.compatibleProperties.filter(f =>
      f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q),
    );
  }

  // ── Save / Remove ─────────────────────────────────────────────────────────

  onSave(): void {
    if (!this.activeRow?.field.id || !this.pendingStdField) return;
    const fieldId = this.activeRow.field.id;
    const mapping: FormFieldCrmMap = {
      crmObjectType:     this.activeTab,
      isCustomAttribute: false,
      crmStandardField:  this.pendingStdField,
      updateRule:        this.pendingUpdateRule,
    };
    this.saving = true;
    this.formsService.saveFieldCrmMap(this.formId, fieldId, mapping)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: saved => {
          this.saving = false;
          this.toast.success('Mapeo guardado');
          this.mappingChanged.emit({ fieldId, mapping: saved });
          if (this.showCatalogPicker) {
            this.fixedValueChanged.emit({ fieldId, value: this.pendingFixedValue });
          }
          // Para campos de selección mapeados a FK de catálogo de COMPANY,
          // cargar y emitir las opciones del catálogo para sincronizarlas con el campo.
          const fieldType = this.activeRow?.field.fieldType;
          const catalog   = this.selectedPropCatalog;
          if (fieldType && ['DROPDOWN', 'CHECKBOXES', 'RADIO'].includes(fieldType)
              && this.activeTab === 'COMPANY' && catalog) {
            this.formsService.getCatalogOptions(catalog).pipe(
              catchError(() => of([] as CatalogOption[])),
              takeUntil(this.destroy$),
            ).subscribe(opts => {
              this.catalogOptionsSync.emit({
                fieldId,
                options: opts.map((o, i) => ({
                  optionLabel: o.label,
                  optionValue: o.value,
                  sortOrder:   i,
                  isDefault:   false,
                })),
              });
              this.closePanel();
            });
          } else {
            this.closePanel();
          }
        },
        error: err => {
          this.saving = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo guardar el mapeo'));
        },
      });
  }

  onRemoveMapping(): void {
    if (!this.activeRow?.field.id) return;
    const fieldId = this.activeRow.field.id;
    this.saving = true;
    this.formsService.deleteFieldCrmMap(this.formId, fieldId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast.success('Mapeo eliminado');
          this.mappingChanged.emit({ fieldId, mapping: null });
          this.closePanel();
        },
        error: err => {
          this.saving = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo eliminar el mapeo'));
        },
      });
  }

  // ── Standard fields loading ───────────────────────────────────────────────

  private ensureStandardFields(objectType: CrmObjectType, onLoaded?: () => void): void {
    if (this.standardFieldsMap[objectType] !== undefined) {
      onLoaded?.();
      return;
    }
    this.formsService.getCrmStandardFields(objectType).pipe(
      catchError(() => of([] as CrmStandardField[])),
      takeUntil(this.destroy$),
    ).subscribe(fields => {
      this.standardFieldsMap = { ...this.standardFieldsMap, [objectType]: fields ?? [] };
      onLoaded?.();
    });
  }

  private preloadMappedObjectTypes(): void {
    const usedTypes = new Set<CrmObjectType>();
    for (const row of this.rows) {
      if (row.mapping?.crmObjectType) usedTypes.add(row.mapping.crmObjectType);
    }
    for (const type of usedTypes) {
      this.ensureStandardFields(type);
    }
  }

  private loadCatalogForProp(key: string): void {
    const fields = this.standardFieldsMap[this.activeTab];
    if (!fields) return;
    const catalog = fields.find(f => f.key === key)?.catalog;
    if (!catalog || this.activeRow?.field.fieldType !== 'HIDDEN') {
      this.catalogOptions = [];
      return;
    }
    if (this.catalogCache[catalog]) {
      this.catalogOptions = this.catalogCache[catalog];
      return;
    }
    this.catalogLoading = true;
    this.formsService.getCatalogOptions(catalog).pipe(
      catchError(() => of([] as CatalogOption[])),
      takeUntil(this.destroy$),
    ).subscribe(opts => {
      this.catalogLoading = false;
      this.catalogCache[catalog] = opts ?? [];
      this.catalogOptions = this.catalogCache[catalog];
    });
  }
}
